import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import {
  ServerPrivateRegionNodeDetails,
  ServerPrivateRegionNodeSpec,
  ServerPrivateRegionVGpuSpec,
} from '@/api/type-server'
import CrossCircleIcon from '@/assets/icons/cross-circle.svg?react'
import { Button, Modal } from '@/components/base'

import { SpecCategory } from '../constants/specCategory'
import { calculateResourceUsage, specToInput } from '../utils/specHelpers'
import { EditableSpecSection } from './EditableSpecSection'
import { ResourceUsageCard } from './ResourceUsageCard'

// Get human-readable names for exceeded resource types
function getExceededResources(resourceUsage: ReturnType<typeof calculateResourceUsage>): string[] {
  const exceeded: string[] = []
  if (resourceUsage.gpu.vgpuUsed + resourceUsage.gpu.gpuUsed > resourceUsage.gpu.total) {
    exceeded.push('Physical GPU')
  }
  if (
    resourceUsage.cpu.vgpuSpecUsed + resourceUsage.cpu.gpuSpecUsed + resourceUsage.cpu.cpuSpecUsed >
    resourceUsage.cpu.total
  ) {
    exceeded.push('vCPU')
  }
  if (
    resourceUsage.ram.vgpuSpecUsed + resourceUsage.ram.gpuSpecUsed + resourceUsage.ram.cpuSpecUsed >
    resourceUsage.ram.total
  ) {
    exceeded.push('Memory')
  }
  if (
    resourceUsage.disk.vgpuSpecUsed +
      resourceUsage.disk.gpuSpecUsed +
      resourceUsage.disk.cpuSpecUsed >
    resourceUsage.disk.total
  ) {
    exceeded.push('Disk')
  }
  return exceeded
}

interface EditInstanceSpecModalProps {
  open: boolean
  node: ServerPrivateRegionNodeDetails
  specs: ServerPrivateRegionNodeSpec[]
  vgpuSpecs: ServerPrivateRegionVGpuSpec[]
  onClose: () => void
  onVerifySuccess: (verifiedSpecs: ServerPrivateRegionNodeSpec[]) => void
}

export const EditInstanceSpecModal = memo(function EditInstanceSpecModal({
  open,
  node,
  specs: initialSpecs,
  vgpuSpecs,
  onClose,
  onVerifySuccess,
}: EditInstanceSpecModalProps) {
  const { t } = useTranslation('privateRegion')
  const [specs, setSpecs] = useState<ServerPrivateRegionNodeSpec[]>(initialSpecs)

  // Sync internal state when modal opens with latest specs
  // Populate vgpuSpecId from nested vGpuSpec.resID if missing
  useEffect(() => {
    if (open) {
      setSpecs(
        initialSpecs.map((s) => {
          if (s.category === SpecCategory.VGPU && !s.vgpuSpecId && s.vGpuSpec?.resID) {
            return {
              ...s,
              vgpuSpecId: s.vGpuSpec.resID,
              gpuRamMB: s.vGpuSpec.vGpuRamMB,
            }
          }
          return s
        }),
      )
    }
  }, [open, initialSpecs])

  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Auto-fill default vGPU GPC for enabled vGPU specs missing selection
  useEffect(() => {
    if (!vgpuSpecs.length) return
    setSpecs((prev) => {
      const hasEmpty = prev.some(
        (s) => s.category === SpecCategory.VGPU && s.enabled && !s.vgpuSpecId,
      )
      if (!hasEmpty) return prev
      const defaultVgpuSpec = vgpuSpecs[0]
      return prev.map((s) => {
        if (s.category === SpecCategory.VGPU && s.enabled && !s.vgpuSpecId) {
          return {
            ...s,
            vgpuSpecId: defaultVgpuSpec.resID,
            gpuNum: 1,
            gpuRamMB: defaultVgpuSpec.vGpuRamMB,
          }
        }
        return s
      })
    })
  }, [vgpuSpecs])

  // Update individual spec
  const handleSpecChange = useCallback((updatedSpec: ServerPrivateRegionNodeSpec) => {
    setSpecs((prev) => prev.map((s) => (s.resID === updatedSpec.resID ? updatedSpec : s)))
    setError(null)
  }, [])

  // Calculate resource usage
  const resourceUsage = useMemo(
    () => calculateResourceUsage(specs, node, vgpuSpecs),
    [specs, node, vgpuSpecs],
  )

  // Check if any resource exceeds limit
  const isOverLimit = useMemo(() => getExceededResources(resourceUsage).length > 0, [resourceUsage])

  // Check if any enabled vGPU spec is missing a GPC selection
  const isMissingVgpuGpc = useMemo(
    () => specs.some((s) => s.category === SpecCategory.VGPU && s.enabled && !s.vgpuSpecId),
    [specs],
  )

  // Check if any enabled spec has empty required fields (value === 0)
  const hasEmptyFields = useMemo(
    () =>
      specs.some((s) => {
        if (!s.enabled) return false
        // Common fields for all categories
        if (s.cpuCores === 0 || s.ramMB === 0 || s.diskGB === 0) return true
        // Units required for all categories
        if (s.units === 0) return true
        // GPU number required for GPU passthrough
        if (s.category === SpecCategory.GPU && s.gpuNum === 0) return true
        return false
      }),
    [specs],
  )

  // Handle verify configuration
  const handleVerify = useCallback(async () => {
    if (isOverLimit) {
      const exceeded = getExceededResources(resourceUsage)
      setError(
        t('editInstanceSpec.exceededResourcesError', {
          resources: exceeded.join(', '),
        }),
      )
      return
    }

    if (isMissingVgpuGpc) {
      setError(t('editInstanceSpec.vgpuGpcRequired'))
      return
    }

    if (hasEmptyFields) {
      setError(t('editInstanceSpec.emptyFieldsError'))
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await API.verifyPrivateRegionSpecs({
        specs: specs.map(specToInput),
      })

      if (response.code !== 0) {
        setError(response.errors?.join(', ') || t('editInstanceSpec.verifyFailed'))
        return
      }

      onVerifySuccess(specs)
    } catch (err) {
      setError(t('editInstanceSpec.verifyFailed'))
    } finally {
      setLoading(false)
    }
  }, [specs, isOverLimit, isMissingVgpuGpc, hasEmptyFields, resourceUsage, onVerifySuccess, t])

  // Filter specs by category
  const vgpuNodeSpecs = specs.filter((spec) => spec.category === SpecCategory.VGPU)
  const gpuSpecs = specs.filter((spec) => spec.category === SpecCategory.GPU)
  const cpuSpecs = specs.filter((spec) => spec.category === SpecCategory.CPU)

  return (
    <Modal open={open} onClose={onClose} className="w-[1344px]">
      <Modal.Header>{t('editInstanceSpec.title')}</Modal.Header>

      <Modal.Body className="flex flex-col gap-4 max-h-[calc(90vh-180px)]">
        {/* Fixed section - Error Alert and Resource Usage Cards */}
        <div className="flex-shrink-0 flex flex-col gap-4">
          {/* Error Alert */}
          {error && (
            <div className="flex items-center gap-4 p-4 bg-error/10 border border-error/40 rounded-lg">
              <CrossCircleIcon className="w-4 h-4 shrink-0 text-error" />
              <p className="t-sm text-error flex-1">{error}</p>
            </div>
          )}

          {/* Resource Usage Overview */}
          <div className="flex gap-3">
            {/* Physical GPU: vGPU (cyan) + GPU Passthrough (blue) */}
            <ResourceUsageCard
              title={t('editInstanceSpec.physicalGpuUsage')}
              vgpuUsed={resourceUsage.gpu.vgpuUsed}
              gpuUsed={resourceUsage.gpu.gpuUsed}
              total={resourceUsage.gpu.total}
              unit="GPU"
              isOverLimit={
                resourceUsage.gpu.vgpuUsed + resourceUsage.gpu.gpuUsed > resourceUsage.gpu.total
              }
            />
            {/* vCPU: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green) */}
            <ResourceUsageCard
              title={t('editInstanceSpec.vcpuUsage')}
              vgpuSpecUsed={resourceUsage.cpu.vgpuSpecUsed}
              gpuSpecUsed={resourceUsage.cpu.gpuSpecUsed}
              cpuSpecUsed={resourceUsage.cpu.cpuSpecUsed}
              total={resourceUsage.cpu.total}
              unit="Cores"
              isOverLimit={
                resourceUsage.cpu.vgpuSpecUsed +
                  resourceUsage.cpu.gpuSpecUsed +
                  resourceUsage.cpu.cpuSpecUsed >
                resourceUsage.cpu.total
              }
            />
            {/* Memory: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green) */}
            <ResourceUsageCard
              title={t('editInstanceSpec.memoryUsage')}
              vgpuSpecUsed={resourceUsage.ram.vgpuSpecUsed}
              gpuSpecUsed={resourceUsage.ram.gpuSpecUsed}
              cpuSpecUsed={resourceUsage.ram.cpuSpecUsed}
              total={resourceUsage.ram.total}
              unit="GB"
              isOverLimit={
                resourceUsage.ram.vgpuSpecUsed +
                  resourceUsage.ram.gpuSpecUsed +
                  resourceUsage.ram.cpuSpecUsed >
                resourceUsage.ram.total
              }
            />
            {/* Disk: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green) */}
            <ResourceUsageCard
              title={t('editInstanceSpec.diskUsage')}
              vgpuSpecUsed={resourceUsage.disk.vgpuSpecUsed}
              gpuSpecUsed={resourceUsage.disk.gpuSpecUsed}
              cpuSpecUsed={resourceUsage.disk.cpuSpecUsed}
              total={resourceUsage.disk.total}
              unit="GB"
              isOverLimit={
                resourceUsage.disk.vgpuSpecUsed +
                  resourceUsage.disk.gpuSpecUsed +
                  resourceUsage.disk.cpuSpecUsed >
                resourceUsage.disk.total
              }
            />
          </div>
        </div>

        {/* Scrollable section - Editable Spec Sections */}
        <div className="flex-1 overflow-y-auto pr-2 flex flex-col gap-4 min-h-0">
          {/* vGPU Spec Sections */}
          {vgpuNodeSpecs.map((spec) => (
            <EditableSpecSection
              key={spec.resID}
              spec={spec}
              category={SpecCategory.VGPU}
              onChange={handleSpecChange}
              vgpuSpecs={vgpuSpecs}
            />
          ))}

          {/* GPU Passthrough Spec Sections */}
          {gpuSpecs.map((spec) => (
            <EditableSpecSection
              key={spec.resID}
              spec={spec}
              category={SpecCategory.GPU}
              onChange={handleSpecChange}
            />
          ))}

          {/* vCPU Spec Sections */}
          {cpuSpecs.map((spec) => (
            <EditableSpecSection
              key={spec.resID}
              spec={spec}
              category={SpecCategory.CPU}
              onChange={handleSpecChange}
            />
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button type="outline" onClick={onClose}>
          {t('common.cancel', { ns: 'translation' })}
        </Button>
        <Button
          type="secondary"
          onClick={handleVerify}
          disabled={isOverLimit || isMissingVgpuGpc || hasEmptyFields || loading}
          className={
            isOverLimit || isMissingVgpuGpc || hasEmptyFields
              ? 'pointer-events-auto cursor-not-allowed'
              : ''
          }
        >
          {loading
            ? t('common.loading', { ns: 'translation' })
            : t('editInstanceSpec.verifyConfiguration')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
})
